/**
 * LE CADRAGE DU BANDEAU — « ce qui doit absolument rester visible ».
 *
 * Le bandeau a une HAUTEUR FIXE et une largeur qui est celle de l'écran : la même
 * image y est donc rognée différemment d'un téléphone à un écran large. Jusqu'ici
 * l'organisateur posait un POINT focal et le navigateur faisait un `cover` autour ;
 * un logo près du bord disparaissait quand même dès que le rapport changeait.
 *
 * Une ZONE remplace le point. La règle tient en deux phrases :
 *   1. la zone est affichée ENTIÈRE, quoi qu'il arrive — on réduit l'image jusqu'à
 *      ce qu'elle tienne plutôt que de la rogner ;
 *   2. s'il reste de la place autour, elle est rendue à l'image, jamais à du vide.
 *
 * Quand l'image, ainsi réduite, ne remplit plus toute la largeur, ses côtés sont
 * peints de la COULEUR DE SON BORD (voir `bandEdge.ts`). Le bandeau reste plein d'un
 * bord à l'autre, et sur un fond uni on ne voit pas où l'image s'arrête.
 *
 * ⚠️ UNE ZONE DE TAILLE NULLE EST L'ANCIEN POINT FOCAL. C'est voulu, et c'est ce qui
 * rend la reprise gratuite : les événements qui n'ont qu'un `..._IMAGE_FOCUS` sont
 * lus comme une zone nulle posée dessus. Aucune migration de données.
 *
 * ⚠️ MAIS CE N'EST PAS `background-position` À LA VIRGULE PRÈS. Le CSS INTERPOLE (le
 * point à 25 % de l'image tombe à 25 % du bandeau) ; ici on CENTRE la zone puis on
 * recolle l'image à ses bords. Les deux coïncident sur les neuf positions nommées du
 * prototype (0, 50 et 100 %), qui sont ce que la grille écrit ; ailleurs le nouveau
 * cadrage ramène le point vers le milieu, de quelques dizaines de pixels au plus.
 * Une seule loi vaut mieux que deux, et celle-ci est celle qu'on peut expliquer :
 * « ce que vous désignez est au centre, ou au plus près du centre que l'image
 * permette ».
 */

/**
 * LA HAUTEUR DU BANDEAU, si l'organisateur l'a choisie (demandé le 2026-09-17).
 *
 * Une seule valeur, celle du bureau ; le mobile en prend les deux tiers, le rapport
 * qu'ont déjà les défauts (120 / 180). Vide, zéro, ou n'importe quoi d'illisible :
 * `null`, et le CSS garde ses défauts. Bornée : en dessous de 40 px un bandeau n'est
 * plus qu'un trait, au-delà de 600 px il mange l'écran d'un téléphone.
 */
export const BAND_HEIGHT_MIN = 40;
export const BAND_HEIGHT_MAX = 600;

export function bandHeights(
  raw: unknown,
): { mobile: number; desktop: number } | null {
  const n =
    typeof raw === "number"
      ? raw
      : typeof raw === "string" && raw.trim() !== ""
        ? Number(raw)
        : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  const desktop = Math.round(clamp(n, BAND_HEIGHT_MIN, BAND_HEIGHT_MAX));
  return { desktop, mobile: Math.round((desktop * 2) / 3) };
}

/**
 * LA HAUTEUR CHOISIE EST UN MAXIMUM, PAS UNE CONSIGNE (tâche #457).
 *
 * Une zone large sur une image basse se heurte à la largeur avant la hauteur :
 * `fitBand` réduit l'image jusqu'à ce que la zone tienne en largeur, et l'image
 * n'atteint plus le haut ni le bas du bandeau. Il restait deux bandes vides, dessus et
 * dessous. C'est alors le bandeau qui descend à la hauteur de l'image.
 *
 * La hauteur que prend l'image dans ce cas ne dépend que de la largeur du bandeau :
 * `largeur × hauteur de l'image / largeur de la zone`. D'où un RAPPORT largeur / hauteur,
 * que le CSS applique en `aspect-ratio` sous un `max-height` qui reste la hauteur
 * choisie : le bandeau prend la plus petite des deux. Au CSS parce que c'est lui qui
 * sait où commence le bureau (une requête `@container`), pas ce calcul.
 *
 * `null` quand l'image remplit toujours la hauteur : sans zone tracée (l'ancien point
 * focal, un `cover` pur), ou tant que ses dimensions ne sont pas connues.
 */
export function bandRatio(image: Box | null, area: SafeArea): number | null {
  if (!image || !(image.width > 0) || !(image.height > 0)) return null;
  const safeWidth = area.w * image.width;
  if (!(safeWidth > 0)) return null;
  return safeWidth / image.height;
}

/** La zone à préserver, en fractions de l'image (0 → 1), coin haut-gauche et taille. */
export type SafeArea = { x: number; y: number; w: number; h: number };

/** Une boîte mesurée, en pixels de la page. */
export type Box = { width: number; height: number };

/** Où poser l'image dans le bandeau, en pixels, et s'il reste des côtés à peindre. */
export type BandFit = {
  width: number;
  height: number;
  left: number;
  top: number;
  /** Vrai quand l'image ne couvre plus le bandeau : la couleur du bord prend le reste. */
  sides: boolean;
  /**
   * De combien de pixels l'image s'efface sur chaque bord pour se marier à l'aplat.
   * Zéro d'un côté qu'elle touche (il n'y a rien à marier) et zéro d'un côté que la
   * zone à préserver atteint : un fondu mangerait justement ce qu'on a promis de
   * montrer.
   */
  fade: { left: number; right: number };
};

/** La largeur du fondu, quand il y a la place de le poser. */
const FADE = 24;

/**
 * Le fondu qui tient dans la place disponible — et rien du tout en dessous du
 * pixel. Une zone qui touche pile le bord de l'image y laisse un jeu de l'ordre de
 * 10⁻¹⁴ px, reste d'un calcul en virgule flottante : c'est zéro, et un dégradé large
 * de zéro virgule rien serait un dégradé qui ne fond pas.
 */
const room = (space: number) => (space < 1 ? 0 : Math.min(space, FADE));

function clamp(n: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, n));
}

const fraction = (n: number) => clamp(n, 0, 1);

/** Arrondi à 0,1 % : de quoi viser un visage, sans écrire « 33.333333% ». */
const round = (n: number) => Math.round(n * 1000) / 10;

/** La valeur telle qu'elle est stockée dans le réglage : « 20% 10% 40% 70% ». */
export function writeSafe(area: SafeArea): string {
  return `${round(area.x)}% ${round(area.y)}% ${round(area.w)}% ${round(area.h)}%`;
}

/** Ramène une zone dans l'image : jamais de coin négatif, jamais de débordement. */
export function normalise(area: SafeArea): SafeArea {
  const x = fraction(area.x);
  const y = fraction(area.y);
  return {
    x,
    y,
    w: clamp(area.w, 0, 1 - x),
    h: clamp(area.h, 0, 1 - y),
  };
}

const PERCENT = /(-?[\d.]+)\s*%/g;

function numbers(value: unknown): number[] {
  if (typeof value !== "string") return [];
  return [...value.matchAll(PERCENT)].map((m) => Number(m[1]) / 100);
}

/**
 * CE QUE L'ORGANISATEUR A DEMANDÉ, zone d'abord, ancien point focal ensuite.
 *
 * Les deux réglages peuvent coexister sur un même événement — l'un vient de la V1 et
 * de la première V2, l'autre du sélecteur de zone. La zone prime, et son absence
 * n'efface pas le point : c'est ce qui permet de l'effacer pour revenir au cadrage
 * d'avant.
 */
export function readSafe(safe: unknown, focus?: unknown): SafeArea {
  const area = parseSafe(safe);
  if (area) return area;
  const [x = 0.5, y = 0.5] = numbers(focus);
  return normalise({ x, y, w: 0, h: 0 });
}

/**
 * LA ZONE TELLE QU'ELLE EST STOCKÉE, ou rien.
 *
 * Le site, lui, veut toujours un cadrage et prend `readSafe`. Le panneau a besoin de
 * la différence : « aucune zone » n'est pas « une zone au centre », c'est ce qui
 * décide entre l'invite à en tracer une et le rectangle à déplacer.
 */
export function parseSafe(value: unknown): SafeArea | null {
  const [x, y, w, h] = numbers(value);
  if (x === undefined || y === undefined || w === undefined || h === undefined)
    return null;
  return normalise({ x, y, w, h });
}

/** Une zone dit-elle vraiment quelque chose, ou n'est-ce qu'un point ? */
export function hasArea(area: SafeArea): boolean {
  return area.w > 0 && area.h > 0;
}

/**
 * LE CALCUL. Une échelle, une position, et la réponse à « reste-t-il du vide ? ».
 *
 * L'échelle est la plus GRANDE des deux qui conviennent : celle qui remplit le
 * bandeau (`cover`), tant que la zone y tient ; sinon celle, plus petite, qui fait
 * tout juste tenir la zone. On ne réduit donc jamais plus que nécessaire.
 *
 * La position centre la ZONE dans le bandeau, puis recolle l'image à ses bords tant
 * qu'elle est assez grande pour les atteindre — sans quoi un cadrage près d'un bord
 * ouvrirait une bande vide que l'image avait de quoi remplir. Quand elle est plus
 * petite que le bandeau, elle est centrée : les deux côtés peints doivent être de
 * même largeur.
 */
export function fitBand(box: Box, image: Box, area: SafeArea): BandFit | null {
  if (
    !(box.width > 0) ||
    !(box.height > 0) ||
    !(image.width > 0) ||
    !(image.height > 0)
  )
    return null;

  const cover = Math.max(box.width / image.width, box.height / image.height);
  const safeWidth = area.w * image.width;
  const safeHeight = area.h * image.height;
  const fits = Math.min(
    safeWidth > 0 ? box.width / safeWidth : Infinity,
    safeHeight > 0 ? box.height / safeHeight : Infinity,
  );
  const scale = Math.min(cover, fits);

  const width = image.width * scale;
  const height = image.height * scale;

  // Le centre de la zone, ramené en pixels du bandeau.
  const centreX = (area.x + area.w / 2) * width;
  const centreY = (area.y + area.h / 2) * height;

  const left =
    width >= box.width
      ? clamp(box.width / 2 - centreX, box.width - width, 0)
      : (box.width - width) / 2;
  const top =
    height >= box.height
      ? clamp(box.height / 2 - centreY, box.height - height, 0)
      : (box.height - height) / 2;

  return {
    width,
    height,
    left,
    top,
    // Un demi-pixel de tolérance : un arrondi de mise en page ne doit pas faire
    // peindre un côté pour une ligne invisible.
    sides: width < box.width - 0.5 || height < box.height - 0.5,
    fade: {
      left: left > 0.5 ? room(area.x * width) : 0,
      right:
        left + width < box.width - 0.5
          ? room((1 - area.x - area.w) * width)
          : 0,
    },
  };
}
